import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import './index.css';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { ToastContainer } from './components/ui/Toast';
import { validateEnvironment, renderEnvError } from './lib/envValidation';
import { logger } from './lib/logger';

// Global unhandled promise rejection handler
// Captures async errors that occur outside React Query's error handling
// (e.g., in event handlers, cleanup functions, third-party libraries)
window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  // Extract error information from the rejection
  const error = event.reason;
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? error.stack : undefined;

  // Log to structured logger
  logger.error('Unhandled promise rejection', error, {
    type: 'unhandledrejection',
    message: errorMessage,
    stack: errorStack,
    url: window.location.href,
  });

  // Sentry captures this automatically via its integration, but we ensure
  // the error is categorized properly with additional context
  if (import.meta.env.PROD && Sentry.isInitialized?.()) {
    Sentry.captureException(error, {
      tags: { errorType: 'unhandledrejection' },
      extra: {
        url: window.location.href,
        timestamp: new Date().toISOString(),
      },
    });
  }

  // Prevent the default browser handling (console error)
  // Our logger already handles this
  event.preventDefault();
});

// Global error handler for uncaught synchronous errors
// Complements the promise rejection handler for complete error coverage
window.addEventListener('error', (event: ErrorEvent) => {
  // Skip errors that are not from scripts (e.g., resource loading errors)
  if (!event.error) return;

  logger.error('Uncaught error', event.error, {
    type: 'uncaughtError',
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    url: window.location.href,
  });

  // Sentry captures this automatically, but we add context
  if (import.meta.env.PROD && Sentry.isInitialized?.()) {
    Sentry.captureException(event.error, {
      tags: { errorType: 'uncaughtError' },
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        url: window.location.href,
      },
    });
  }
});

// Validate environment variables before anything else
const envValidation = validateEnvironment();
if (!envValidation.isValid) {
  // Render error screen and stop execution
  renderEnvError(envValidation);
  throw new Error(
    `Missing required environment variables: ${envValidation.missingRequired.join(', ')}. ` +
    'See the error screen for details.'
  );
}

// Log warnings for missing optional variables in development
if (import.meta.env.DEV && envValidation.missingOptional.length > 0) {
  logger.warn('Optional environment variables not set', {
    missing: envValidation.missingOptional
  });
}

// Conditionally import React Query DevTools only in development
const ReactQueryDevtools = import.meta.env.DEV
  ? React.lazy(() =>
      import('@tanstack/react-query-devtools').then((mod) => ({
        default: mod.ReactQueryDevtools,
      }))
    )
  : () => null;

// Configure React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000,   // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Initialize Sentry
if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
} else {
  logger.debug('Sentry not initialized: Not in PROD or missing VITE_SENTRY_DSN');
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = ReactDOM.createRoot(rootElement);
logger.debug('React root created');

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <ToastProvider>
            <App />
            <ToastContainer />
          </ToastProvider>
        </ThemeProvider>
      </BrowserRouter>
      {import.meta.env.DEV && (
        <React.Suspense fallback={null}>
          <ReactQueryDevtools initialIsOpen={false} />
        </React.Suspense>
      )}
    </QueryClientProvider>
  </React.StrictMode>
);